import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getDeviceHash } from "@/utils/deviceDetection";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";

interface VoteButtonProps {
  listingId: string;
  citySlug: string;
  activitySlug: string;
  currentVotes?: number;
  onVoteSuccess?: (newTotal: number) => void;
}

export const VoteButton = ({ 
  listingId, 
  citySlug, 
  activitySlug,
  currentVotes = 0,
  onVoteSuccess 
}: VoteButtonProps) => {
  const [isVoting, setIsVoting] = useState(false);
  const [votes, setVotes] = useState(currentVotes);
  const [hasVoted, setHasVoted] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const handleVote = async () => {
    if (isVoting || hasVoted) return;
    
    setIsVoting(true);
    
    try {
      const deviceHash = getDeviceHash();
      const { data: { user } } = await supabase.auth.getUser();
      
      // Check if user is not authenticated and has already voted
      if (!user) {
        const guestVoteCount = parseInt(localStorage.getItem('guest_vote_count') || '0');
        if (guestVoteCount >= 1) {
          setShowAuthDialog(true);
          setIsVoting(false);
          return;
        }
      }
      
      // Call the voting edge function
      const { data, error } = await supabase.functions.invoke('vote', {
        body: {
          listingId,
          citySlug,
          activitySlug,
          deviceHash,
          userId: user?.id
        }
      });

      if (error) throw error;
      
      if (data?.error) {
        toast.error(data.error);
        return;
      }

      // Optimistic UI update
      const newTotal = data.newTotal || votes + 1;
      setVotes(newTotal);
      setHasVoted(true);
      onVoteSuccess?.(newTotal);
      
      toast.success("¡Voto registrado! 🎉", {
        description: "Gracias por apoyar este lugar"
      });
      
      // Store vote locally
      localStorage.setItem(`voted_${listingId}_${citySlug}_${activitySlug}`, 'true');
      
      // Increment guest vote count if not authenticated
      if (!user) {
        const currentCount = parseInt(localStorage.getItem('guest_vote_count') || '0');
        localStorage.setItem('guest_vote_count', String(currentCount + 1));
      }
      
    } catch (error: any) {
      console.error('Vote error:', error);
      toast.error("Error al votar", {
        description: error.message || "Intenta de nuevo más tarde"
      });
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleVote}
        disabled={isVoting || hasVoted}
        variant={hasVoted ? "secondary" : "default"}
        size="lg"
        className="w-full gap-2 font-bold text-base py-6 shadow-lg hover:shadow-xl transition-all hover:scale-105"
      >
        <Heart className={`w-5 h-5 ${hasVoted ? 'fill-current' : ''}`} />
        {hasVoted ? '✓ Votado' : '👍 Votar'}
        <span className="ml-2 font-bold">({votes})</span>
      </Button>

      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regístrate para votar más veces</DialogTitle>
            <DialogDescription className="space-y-4">
              <p>
                Has alcanzado el límite de votos como visitante. Regístrate gratis para votar sin límites y apoyar a tus lugares favoritos.
              </p>
              <div className="flex flex-col gap-3 pt-4">
                <Button asChild className="w-full">
                  <Link to="/auth?mode=signup">Crear cuenta gratis</Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/auth?mode=signin">Ya tengo cuenta</Link>
                </Button>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};
